/**
 * Application Routing Configuration
 * 
 * This file defines all the routes for the parking management system application.
 * It includes routes for both admin and client applications, with proper authentication
 * guards and role-based access control.
 * 
 * Route Structure:
 * - Public routes: login, signup (accessible without authentication)
 * - Admin routes: organization management, billing, subscriptions, plans
 * - Client routes: parking management, dashboard, reports, settings
 * - Protected routes: all routes except public ones require authentication
 * 
 * Guards Used:
 * - authGuard: Ensures user is authenticated
 * - loginGuard: Redirects authenticated users away from login page
 * - publicGuard: Allows access to public pages
 * - roleGuard: Enforces role-based access control
 * - redirectGuard: Handles redirects based on user state
 * 
 * @author Parking System Team
 * @version 1.0.0
 */

import { Routes, RouterModule } from '@angular/router';
import { preventWizardBackGuard } from './shared/guards/prevent-wizard-back.guard';

// Admin Application Components - Super Admin & Organization Management
// Lazy loaded: import { Billing } from './admin_app/modules/billing/billing';
// Lazy loaded: import { Subscriptions } from './admin_app/modules/subscriptions/subscriptions';
// Lazy loaded: import { Plans } from './admin_app/modules/plans/plans';
// Lazy loaded: import { SubPrice } from './admin_app/modules/sub-price/sub-price';
// Lazy loaded: import { Subscribers } from './admin_app/modules/subscribers/subscribers';
// Lazy loaded: import { Transactions } from './admin_app/modules/transactions/transactions';
// Lazy loaded: import { AllOrg } from './admin_app/modules/all-org/all-org';
// Lazy loaded: import Dashboard from './admin_app/modules/dashboard/dashboard';
import Parkings from './client_app/admin/parkings/parkings';

// Authentication Components
// Lazy loaded: import Login from './auth/login/login';
// Lazy loaded: import { SignUp } from './auth/sign-up/sign-up';

// Shared Components & Utilities
// Lazy loaded: import { GlobalTimezoneSettingsComponent } from './shared/components/global-timezone-settings/global-timezone-settings';
// Lazy loaded: import { TimezoneDemoComponent } from './shared/components/timezone-demo/timezone-demo';
// Lazy loaded: import { GlobalSettingsComponent } from './shared/components/global-settings/global-settings';
// Lazy loaded: import { RoleNavigationDemoComponent } from './shared/components/role-navigation-demo/role-navigation-demo';
// Lazy loaded: import { RoleSwitcherDebugComponent } from './shared/components/role-switcher-debug/role-switcher-debug';
// Lazy loaded: import { RoleSwitcherTestComponent } from './shared/components/role-switcher-test/role-switcher-test';

// Route Guards - Security & Access Control
import { authGuard } from './shared/guards/auth.guard';
import { loginGuard } from './shared/guards/login.guard';
import { redirectGuard } from './shared/guards/redirect.guard';
import { RoleGuard } from './shared/guards/role.guard';
import { publicGuard } from './shared/guards/public.guard';
import { PermissionGuard } from './shared/guards/permission.guard';
import { ParkingRedirectGuard } from './shared/guards/parking-redirect.guard';
import { nativePosOverviewGuard } from './shared/guards/native-pos-overview.guard';

// Admin Settings Components
import { ProfileSetting } from './admin_app/settings/profile-setting/profile-setting';
import SettingsMain from './admin_app/settings/settings-main/settings-main';
import Regions from './admin_app/settings/regions/regions';
import Team from './admin_app/settings/team/team';
import Facilities from './admin_app/settings/facilities/facilities';
// Lazy loaded: import AdminPasses from './admin_app/settings/passes/passes';
// Lazy loaded: import VehicleRate from './admin_app/settings/vehicle-rate/vehicle-rate';
import Integrations from './admin_app/settings/integrations/integrations';
// Lazy loaded: import Devices from './admin_app/settings/devices/devices';
// Lazy loaded: import { ThermalPrinterComponent } from './admin_app/settings/thermal-printer/thermal-printer';

// Client Application Components - Core Features
// Lazy loaded: import { Session } from './client_app/session/session';
// Lazy loaded: import { Facilities as ClientFacilities } from './client_app/facilities/facilities';
// Lazy loaded: import { FacilityActivities } from './client_app/facility-activities/facility-activities';
// Lazy loaded: import { Passes as ClientPasses } from './client_app/passes/passes';
// Lazy loaded: import { PassesDetails } from './client_app/passes-details/passes-details';
// Lazy loaded: import { Customers } from './client_app/customers/customers';
// Lazy loaded: import { AI } from './client_app/ai/ai';
// Lazy loaded: import { OrganizationProfile } from './client_app/organization-profile/organization-profile';
// Lazy loaded: import { Team as AdminTeam } from './client_app/team/team';
// Lazy loaded: import Taxes from './client_app/taxes/taxes';
// Lazy loaded: import PaymentGateway from './client_app/payment-gateway/payment-gateway';
// Lazy loaded: import { Dashboard as adminDashboard } from './client_app/dashboard/dashboard';
// Lazy loaded: import ParkingAddPeople from './client_app/parking-add-people/parking-add-people';
// Lazy loaded: import { ParkingAddCapacity } from './client_app/parking-add-capacity/parking-add-capacity';
// Lazy loaded: import ReviewParking from './client_app/review-parking/review-parking';
// Lazy loaded: import { ParkingLots } from './client_app/parking-lots/parking-lots';
// Lazy loaded: import { Reports } from './client_app/reports/reports';
// Lazy loaded: import { ParkingProfile } from './client_app/parking-profile/parking-profile';
// Lazy loaded: import { UpgradePlan } from './client_app/upgrade-plan/upgrade-plan';
// Lazy loaded: import Reservations from './client_app/reservations/reservations';
// Lazy loaded: import { Invoice } from './client_app/invoice/invoice';
// Lazy loaded: import { SetupGuide } from './client_app/setup-guide/setup-guide';
// Lazy loaded: import { ReportView } from './client_app/report-view/report-view';
// Lazy loaded: import { AdminFullReport } from './client_app/admin-full-report/admin-full-report';

// Additional Admin Components
// Lazy loaded: import { Subscribers as SubscriptionsPlan } from './admin_app/modules/subscriptions-plan/subscriptions-plan';
// Lazy loaded: import NotFound from './not-found/not-found';
// Lazy loaded: import NoParking from './no-parking/no-parking';
// Lazy loaded: import NoOrganization from './no-organization/no-organization';
// Lazy loaded: import NetworkError from './network-error/network-error';
// Lazy loaded: import { TicketList } from './zoho_support/ticket-list/ticket-list';
// Lazy loaded: import { Reply } from './zoho_support/reply/reply';
// Lazy loaded: import { NewTicket } from './zoho_support/new-ticket/new-ticket';
// Lazy loaded: import { PosSession } from './pos/pos-session/pos-session';
// Lazy loaded: import { PosPasses } from './pos/pos-passes/pos-passes';
// Lazy loaded: import { PosFacilities } from './pos/pos-facilities/pos-facilities';


/**
 * Main Routes Configuration
 * 
 * Defines all application routes with proper authentication and authorization.
 * Routes are organized by functionality and access level.
 */
export const routes: Routes = [
  // ==================== PUBLIC ROUTES ====================
  // Routes accessible without authentication

  /** 
   * Root route - redirects to login page
   * Ensures unauthenticated users are directed to login
   */
  {
    path: '',
    redirectTo: '/login',
    pathMatch: 'full'
  },

  /**
   * User registration page
   * Allows new users to create accounts
   */
  {
    path: 'signup',
    loadComponent: () => import('./auth/sign-up/sign-up').then(m => m.SignUp),
    title: 'Create Account | Register for Parking Management',
    pathMatch: 'full',
    canActivate: [publicGuard]
  },
  {
    path: 'sign-up',
    redirectTo: '/signup',
    pathMatch: 'full'
  },

  /**
   * User login page
   * Authenticates users and redirects based on role
   */
  {
    path: 'login',
    loadComponent: () => import('./auth/login/login').then(m => m.default),
    title: 'Login | Parking Management System',
    pathMatch: 'full',
    canActivate: [loginGuard]
  },

  /**
   * Network error page
   * Shows when there's no internet connection
   */
  {
    path: 'network-error',
    loadComponent: () => import('./network-error/network-error').then(m => m.default),
    title: 'Connection Lost | Parking System Error',
    pathMatch: 'full'
  },

  // ==================== NEW URL FORMAT ROUTES ====================
  // Routes following the new URL structure:
  // 1. base-url/parking/{parking-id}/page-name (specific routes - must come first)
  // 2. base-url/{user_pov}/{org-id}/page-name (dynamic based on user_pov value)
  // 3. base-url/org/{org-id}/page-name (backward compatibility)
  // 4. base-url/org/{org-id}/page-name/secondary-page-name

  /**
   * Parking-scoped routes: /parking/:parkingId/:pageName
   * These MUST come before dynamic :pov routes to ensure correct matching
   * Supports parking-level pages
   */
  {
    path: 'parking/:parkingId/dashboard',
    loadComponent: () => import('./client_app/dashboard/dashboard').then(m => m.Dashboard),
    title: 'Dashboard | Parking Management System',
    canActivate: [nativePosOverviewGuard, authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/pos',
    loadComponent: () => import('./pos/pos-overview/pos-overview').then(m => m.PosOverview),
    title: 'POS Overview | Parking Management System',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/pos-session',
    loadComponent: () => import('./pos/pos-session/pos-session').then(m => m.PosSession),
    title: 'POS Session | Parking Point of Sale',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/pos-overview',
    loadComponent: () => import('./pos/pos-overview/pos-overview').then(m => m.PosOverview),
    title: 'POS Overview | Parking Management System',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/pos-dashboard',
    loadComponent: () => import('./pos/pos-overview/pos-overview').then(m => m.PosOverview),
    title: 'POS Overview | Parking Management System',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/pos-passes',
    loadComponent: () => import('./pos/pos-passes/pos-passes').then(m => m.PosPasses),
    title: 'POS Passes | Manage Permits',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/pos-facilities',
    loadComponent: () => import('./pos/pos-facilities/pos-facilities').then(m => m.PosFacilities),
    title: 'POS Facilities | Real-time Check-in',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/parking-lots',
    loadComponent: () => import('./client_app/parking-lots/parking-lots').then(m => m.ParkingLots),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/session',
    loadComponent: () => import('./client_app/session/session').then(m => m.Session),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/vehicle-sessions',
    loadComponent: () => import('./client_app/session/session').then(m => m.Session),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/passes',
    loadComponent: () => import('./client_app/passes/passes').then(m => m.Passes),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/customers',
    loadComponent: () => import('./client_app/customers/customers').then(m => m.Customers),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/customer',
    loadComponent: () => import('./client_app/customers/customers').then(m => m.Customers),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/linked-vehicles',
    loadComponent: () => import('./client_app/customers/customers').then(m => m.Customers),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/reports',
    loadComponent: () => import('./client_app/reports/reports').then(m => m.Reports),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/report',
    loadComponent: () => import('./client_app/reports/reports').then(m => m.Reports),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'organization/:orgId/admin-full-report',
    loadComponent: () => import('./client_app/admin-full-report/admin-full-report').then(m => m.AdminFullReport),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/report-view',
    loadComponent: () => import('./client_app/report-view/report-view').then(m => m.ReportView),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/data-exports',
    loadComponent: () => import('./client_app/reports/reports').then(m => m.Reports),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/data-import',
    loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport),
    title: 'Data Import | Parking Management System',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/dataimport',
    redirectTo: 'parking/:parkingId/data-import',
    pathMatch: 'full'
  },
  {
    path: 'parking/:parkingId/data-export',
    redirectTo: 'parking/:parkingId/settings/data-export',
    pathMatch: 'full'
  },
  {
    path: 'parking/:parkingId/dataexport',
    redirectTo: 'parking/:parkingId/settings/data-export',
    pathMatch: 'full'
  },
  {
    path: 'parking/:parkingId/reservations',
    loadComponent: () => import('./client_app/reservations/reservations').then(m => m.default),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/reservation',
    loadComponent: () => import('./client_app/reservations/reservations').then(m => m.default),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/facilities',
    loadComponent: () => import('./client_app/facilities/facilities').then(m => m.Facilities),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/facility-activities',
    loadComponent: () => import('./client_app/facility-activities/facility-activities').then(m => m.FacilityActivities),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/facility-usage-logs',
    loadComponent: () => import('./client_app/facility-activities/facility-activities').then(m => m.FacilityActivities),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/ai',
    loadComponent: () => import('./client_app/ai/ai').then(m => m.AI),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/sistem-ai-insight',
    loadComponent: () => import('./client_app/ai/ai').then(m => m.AI),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/parking-profile',
    loadComponent: () => import('./client_app/parking-profile/parking-profile').then(m => m.ParkingProfile),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/profile',
    component: ProfileSetting,
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/billing',
    loadComponent: () => import('./client_app/invoice/invoice').then(m => m.Invoice),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/billing-invoice',
    loadComponent: () => import('./client_app/invoice/invoice').then(m => m.Invoice),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/setup-guide',
    loadComponent: () => import('./client_app/setup-guide/setup-guide').then(m => m.SetupGuide),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'parking/:parkingId/upgrade-plan',
    loadComponent: () => import('./client_app/upgrade-plan/upgrade-plan').then(m => m.UpgradePlan),
    canActivate: [authGuard]
  },
  {
    path: 'parking/:parkingId/settings',
    component: SettingsMain,
    canActivate: [authGuard, PermissionGuard],
    children: [
      { path: '', component: Regions },
      { path: 'regions', component: Regions },
      { path: 'regions/:id', component: Regions },
      { path: 'language', loadComponent: () => import('./admin_app/settings/language/language').then(m => m.default) },
      { path: 'language/:id', loadComponent: () => import('./admin_app/settings/language/language').then(m => m.default) },
      { path: 'org-profile', loadComponent: () => import('./client_app/organization-profile/organization-profile').then(m => m.OrganizationProfile) },
      { path: 'org-profile/:id', loadComponent: () => import('./client_app/organization-profile/organization-profile').then(m => m.OrganizationProfile) },
      { path: 'organization-profile', loadComponent: () => import('./client_app/organization-profile/organization-profile').then(m => m.OrganizationProfile) },
      { path: 'organization-profile/:id', loadComponent: () => import('./client_app/organization-profile/organization-profile').then(m => m.OrganizationProfile) },
      { path: 'parking-profile', loadComponent: () => import('./client_app/parking-profile/parking-profile').then(m => m.ParkingProfile) },
      { path: 'parking-profile/:parkingId', loadComponent: () => import('./client_app/parking-profile/parking-profile').then(m => m.ParkingProfile) },
      { path: 'parking-profile/:parkingId/:parkingName', loadComponent: () => import('./client_app/parking-profile/parking-profile').then(m => m.ParkingProfile) },
      { path: 'team', component: Team, canActivate: [PermissionGuard, RoleGuard] },
      { path: 'team/:id', component: Team, canActivate: [PermissionGuard, RoleGuard] },
      { path: 'passes', loadComponent: () => import('./admin_app/settings/passes/passes').then(m => m.Passes) },
      { path: 'passes/:id', loadComponent: () => import('./admin_app/settings/passes/passes').then(m => m.Passes) },
      { path: 'pass-rates', loadComponent: () => import('./admin_app/settings/passes/passes').then(m => m.Passes) },
      { path: 'pass-rates/:id', loadComponent: () => import('./admin_app/settings/passes/passes').then(m => m.Passes) },
      { path: 'plans', component: ProfileSetting },
      { path: 'plans/:id', component: ProfileSetting },
      { path: 'vehicle-rate', loadComponent: () => import('./admin_app/settings/vehicle-rate/vehicle-rate').then(m => m.default) },
      { path: 'vehicle-rate/:id', loadComponent: () => import('./admin_app/settings/vehicle-rate/vehicle-rate').then(m => m.default) },
      { path: 'vehicle-rates', loadComponent: () => import('./admin_app/settings/vehicle-rate/vehicle-rate').then(m => m.default) },
      { path: 'vehicle-rates/:id', loadComponent: () => import('./admin_app/settings/vehicle-rate/vehicle-rate').then(m => m.default) },
      { path: 'facilities', component: Facilities },
      { path: 'facilities/:id', component: Facilities },
      { path: 'facility-rates', component: Facilities },
      { path: 'facility-rates/:id', component: Facilities },
      { path: 'facility-setup', component: Facilities },
      { path: 'facility-setup/:id', component: Facilities },
      { path: 'integrations', component: Integrations },
      { path: 'integrations/:id', component: Integrations },
      { path: 'hardware-integration', component: Integrations },
      { path: 'hardware-integration/:id', component: Integrations },
      { path: 'anpr-module', component: Integrations },
      { path: 'anpr-module/:id', component: Integrations },
      { path: 'devices', loadComponent: () => import('./admin_app/settings/devices/devices').then(m => m.default) },
      { path: 'devices/:id', loadComponent: () => import('./admin_app/settings/devices/devices').then(m => m.default) },
      { path: 'thermal-printer', loadComponent: () => import('./admin_app/settings/thermal-printer/thermal-printer').then(m => m.ThermalPrinterComponent) },
      { path: 'thermal-printer/:id', loadComponent: () => import('./admin_app/settings/thermal-printer/thermal-printer').then(m => m.ThermalPrinterComponent) },
      { path: 'payment-options', loadComponent: () => import('./client_app/payment-gateway/payment-gateway').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'payment-options/:id', loadComponent: () => import('./client_app/payment-gateway/payment-gateway').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'payment-gateway', loadComponent: () => import('./client_app/payment-gateway/payment-gateway').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'payment-gateway/:id', loadComponent: () => import('./client_app/payment-gateway/payment-gateway').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'taxes', loadComponent: () => import('./client_app/taxes/taxes').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'taxes/:id', loadComponent: () => import('./client_app/taxes/taxes').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'profile/:parkingId', component: ProfileSetting, canActivate: [authGuard, PermissionGuard] },
      { path: 'media-branding', component: ProfileSetting },
      { path: 'media-branding/:id', component: ProfileSetting },
      { path: 'data-import', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'data-import/:id', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'dataimport', redirectTo: 'data-import', pathMatch: 'full' },
      { path: 'dataimport/:id', redirectTo: 'data-import/:id', pathMatch: 'full' },
      { path: 'import-historical-data', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'import-historical-data/:id', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'data-export', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: 'data-export/:id', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: 'dataexport', redirectTo: 'data-export', pathMatch: 'full' },
      { path: 'dataexport/:id', redirectTo: 'data-export/:id', pathMatch: 'full' },
      { path: 'export-historical-data', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: 'export-historical-data/:id', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: ':id', component: Regions },
    ]
  },

  /**
   * Dynamic user_pov-scoped routes: /:pov/:orgId/:pageName
   * Supports dynamic prefixes based on user_pov value (e.g., "organization", "parking")
   * These routes will match any pov value EXCEPT "parking" (which is handled above)
   */
  {
    path: ':pov/:orgId/dashboard',
    loadComponent: () => import('./client_app/dashboard/dashboard').then(m => m.Dashboard),
    title: 'Dashboard | Parking Management System',
    canActivate: [nativePosOverviewGuard, authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/parking-lots',
    loadComponent: () => import('./client_app/parking-lots/parking-lots').then(m => m.ParkingLots),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/session',
    loadComponent: () => import('./client_app/session/session').then(m => m.Session),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/vehicle-sessions',
    loadComponent: () => import('./client_app/session/session').then(m => m.Session),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/passes',
    loadComponent: () => import('./client_app/passes/passes').then(m => m.Passes),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/customers',
    loadComponent: () => import('./client_app/customers/customers').then(m => m.Customers),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/customer',
    loadComponent: () => import('./client_app/customers/customers').then(m => m.Customers),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/linked-vehicles',
    loadComponent: () => import('./client_app/customers/customers').then(m => m.Customers),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/reports',
    loadComponent: () => import('./client_app/reports/reports').then(m => m.Reports),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/report',
    loadComponent: () => import('./client_app/reports/reports').then(m => m.Reports),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/admin-full-report',
    loadComponent: () => import('./client_app/admin-full-report/admin-full-report').then(m => m.AdminFullReport),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/report-view',
    loadComponent: () => import('./client_app/report-view/report-view').then(m => m.ReportView),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/data-exports',
    loadComponent: () => import('./client_app/reports/reports').then(m => m.Reports),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/data-import',
    loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport),
    title: 'Data Import | Parking Management System',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/dataimport',
    redirectTo: ':pov/:orgId/data-import',
    pathMatch: 'full'
  },
  {
    path: ':pov/:orgId/data-export',
    redirectTo: ':pov/:orgId/settings/data-export',
    pathMatch: 'full'
  },
  {
    path: ':pov/:orgId/dataexport',
    redirectTo: ':pov/:orgId/settings/data-export',
    pathMatch: 'full'
  },
  {
    path: ':pov/:orgId/reservations',
    loadComponent: () => import('./client_app/reservations/reservations').then(m => m.default),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/reservation',
    loadComponent: () => import('./client_app/reservations/reservations').then(m => m.default),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/facilities',
    loadComponent: () => import('./client_app/facilities/facilities').then(m => m.Facilities),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/facility-activities',
    loadComponent: () => import('./client_app/facility-activities/facility-activities').then(m => m.FacilityActivities),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/facility-usage-logs',
    loadComponent: () => import('./client_app/facility-activities/facility-activities').then(m => m.FacilityActivities),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/ai',
    loadComponent: () => import('./client_app/ai/ai').then(m => m.AI),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/sistem-ai-insight',
    loadComponent: () => import('./client_app/ai/ai').then(m => m.AI),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/team',
    loadComponent: () => import('./client_app/team/team').then(m => m.Team),
    canActivate: [authGuard, PermissionGuard, RoleGuard]
  },
  {
    path: ':pov/:orgId/billing',
    loadComponent: () => import('./client_app/invoice/invoice').then(m => m.Invoice),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/billing-invoice',
    loadComponent: () => import('./client_app/invoice/invoice').then(m => m.Invoice),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/setup-guide',
    loadComponent: () => import('./client_app/setup-guide/setup-guide').then(m => m.SetupGuide),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/upgrade-plan',
    loadComponent: () => import('./client_app/upgrade-plan/upgrade-plan').then(m => m.UpgradePlan),
    canActivate: [authGuard]
  },
  {
    path: ':pov/:orgId/subscriptions',
    loadComponent: () => import('./admin_app/modules/subscriptions/subscriptions').then(m => m.Subscriptions),
    title: 'Subscriptions | Manage Plan',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/plans',
    loadComponent: () => import('./admin_app/modules/plans/plans').then(m => m.Plans),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/organization-profile',
    loadComponent: () => import('./client_app/organization-profile/organization-profile').then(m => m.OrganizationProfile),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/profile',
    component: ProfileSetting,
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: ':pov/:orgId/settings',
    component: SettingsMain,
    canActivate: [authGuard, PermissionGuard],
    children: [
      { path: '', component: Regions },
      { path: 'regions', component: Regions },
      { path: 'regions/:id', component: Regions },
      { path: 'language', loadComponent: () => import('./admin_app/settings/language/language').then(m => m.default) },
      { path: 'language/:id', loadComponent: () => import('./admin_app/settings/language/language').then(m => m.default) },
      { path: 'org-profile', loadComponent: () => import('./client_app/organization-profile/organization-profile').then(m => m.OrganizationProfile) },
      { path: 'org-profile/:id', loadComponent: () => import('./client_app/organization-profile/organization-profile').then(m => m.OrganizationProfile) },
      { path: 'organization-profile', loadComponent: () => import('./client_app/organization-profile/organization-profile').then(m => m.OrganizationProfile) },
      { path: 'organization-profile/:id', loadComponent: () => import('./client_app/organization-profile/organization-profile').then(m => m.OrganizationProfile) },
      { path: 'parking-profile', loadComponent: () => import('./client_app/parking-profile/parking-profile').then(m => m.ParkingProfile) },
      { path: 'parking-profile/:parkingId', loadComponent: () => import('./client_app/parking-profile/parking-profile').then(m => m.ParkingProfile) },
      { path: 'parking-profile/:parkingId/:parkingName', loadComponent: () => import('./client_app/parking-profile/parking-profile').then(m => m.ParkingProfile) },
      { path: 'team', component: Team, canActivate: [PermissionGuard, RoleGuard] },
      { path: 'team/:id', component: Team, canActivate: [PermissionGuard, RoleGuard] },
      { path: 'passes', loadComponent: () => import('./admin_app/settings/passes/passes').then(m => m.Passes) },
      { path: 'passes/:id', loadComponent: () => import('./admin_app/settings/passes/passes').then(m => m.Passes) },
      { path: 'pass-rates', loadComponent: () => import('./admin_app/settings/passes/passes').then(m => m.Passes) },
      { path: 'pass-rates/:id', loadComponent: () => import('./admin_app/settings/passes/passes').then(m => m.Passes) },
      { path: 'plans', component: ProfileSetting },
      { path: 'plans/:id', component: ProfileSetting },
      { path: 'vehicle-rate', loadComponent: () => import('./admin_app/settings/vehicle-rate/vehicle-rate').then(m => m.default) },
      { path: 'vehicle-rate/:id', loadComponent: () => import('./admin_app/settings/vehicle-rate/vehicle-rate').then(m => m.default) },
      { path: 'vehicle-rates', loadComponent: () => import('./admin_app/settings/vehicle-rate/vehicle-rate').then(m => m.default) },
      { path: 'vehicle-rates/:id', loadComponent: () => import('./admin_app/settings/vehicle-rate/vehicle-rate').then(m => m.default) },
      { path: 'facilities', component: Facilities },
      { path: 'facilities/:id', component: Facilities },
      { path: 'facility-rates', component: Facilities },
      { path: 'facility-rates/:id', component: Facilities },
      { path: 'facility-setup', component: Facilities },
      { path: 'facility-setup/:id', component: Facilities },
      { path: 'integrations', component: Integrations },
      { path: 'integrations/:id', component: Integrations },
      { path: 'hardware-integration', component: Integrations },
      { path: 'hardware-integration/:id', component: Integrations },
      { path: 'anpr-module', component: Integrations },
      { path: 'anpr-module/:id', component: Integrations },
      { path: 'devices', loadComponent: () => import('./admin_app/settings/devices/devices').then(m => m.default) },
      { path: 'devices/:id', loadComponent: () => import('./admin_app/settings/devices/devices').then(m => m.default) },
      { path: 'thermal-printer', loadComponent: () => import('./admin_app/settings/thermal-printer/thermal-printer').then(m => m.ThermalPrinterComponent) },
      { path: 'thermal-printer/:id', loadComponent: () => import('./admin_app/settings/thermal-printer/thermal-printer').then(m => m.ThermalPrinterComponent) },
      { path: 'payment-options', loadComponent: () => import('./client_app/payment-gateway/payment-gateway').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'payment-options/:id', loadComponent: () => import('./client_app/payment-gateway/payment-gateway').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'payment-gateway', loadComponent: () => import('./client_app/payment-gateway/payment-gateway').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'payment-gateway/:id', loadComponent: () => import('./client_app/payment-gateway/payment-gateway').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'taxes', loadComponent: () => import('./client_app/taxes/taxes').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'taxes/:id', loadComponent: () => import('./client_app/taxes/taxes').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'profile/:parkingId', component: ProfileSetting, canActivate: [authGuard, PermissionGuard] },
      { path: 'media-branding', component: ProfileSetting },
      { path: 'media-branding/:id', component: ProfileSetting },
      { path: 'data-import', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'data-import/:id', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'dataimport', redirectTo: 'data-import', pathMatch: 'full' },
      { path: 'dataimport/:id', redirectTo: 'data-import/:id', pathMatch: 'full' },
      { path: 'import-historical-data', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'import-historical-data/:id', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'data-export', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: 'data-export/:id', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: 'dataexport', redirectTo: 'data-export', pathMatch: 'full' },
      { path: 'dataexport/:id', redirectTo: 'data-export/:id', pathMatch: 'full' },
      { path: 'export-historical-data', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: 'export-historical-data/:id', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: ':id', component: Regions },
    ]
  },

  // ==================== PROTECTED ROUTES ====================
  // Routes requiring authentication
  // Note: Old format routes without POV prefix are removed - use dynamic POV routes instead


  /**
   * Admin dashboard - super admin access
   * System-wide administration and management
   * Note: This is a special route for super admin, not POV-scoped
   */
  {
    path: 'admin-dashboard',
    loadComponent: () => import('./admin_app/modules/dashboard/dashboard').then(m => m.default),
    canActivate: [authGuard, PermissionGuard]
  },
  // Organization dashboard route without orgId - for super admin only
  // This must come before the :pov/:orgId/dashboard route to prevent matching
  {
    path: 'organization/dashboard',
    loadComponent: () => import('./client_app/dashboard/dashboard').then(m => m.Dashboard),
    canActivate: [nativePosOverviewGuard, authGuard, PermissionGuard]
  },
  // Simple dashboard route without POV prefix
  {
    path: 'dashboard',
    loadComponent: () => import('./client_app/dashboard/dashboard').then(m => m.Dashboard),
    canActivate: [nativePosOverviewGuard, authGuard, PermissionGuard]
  },
  // Simple pos-overview route without POV prefix
  {
    path: 'pos-overview',
    loadComponent: () => import('./pos/pos-overview/pos-overview').then(m => m.PosOverview),
    title: 'POS Overview | Parking Management System',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'pos',
    loadComponent: () => import('./pos/pos-overview/pos-overview').then(m => m.PosOverview),
    title: 'POS Overview | Parking Management System',
    canActivate: [authGuard, PermissionGuard]
  },
  // Simple billing route without POV prefix
  {
    path: 'billing',
    loadComponent: () => import('./client_app/invoice/invoice').then(m => m.Invoice),
    canActivate: [authGuard, PermissionGuard]
  },
  // Simple upgrade-plan route without POV prefix
  {
    path: 'upgrade-plan',
    loadComponent: () => import('./client_app/upgrade-plan/upgrade-plan').then(m => m.UpgradePlan),
    canActivate: [authGuard]
  },
  // Simple profile routes without POV prefix
  {
    path: 'profile',
    component: ProfileSetting,
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'profile/:parkingId',
    component: ProfileSetting,
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'help-center/activities',
    loadComponent: () => import('./zoho_support/ticket-list/ticket-list').then(m => m.TicketList),
    canActivate: [authGuard]
  },
  {
    path: 'help-center/activities/new-ticket',
    loadComponent: () => import('./zoho_support/new-ticket/new-ticket').then(m => m.NewTicket),
    canActivate: [authGuard]
  },
  {
    path: 'help-center/activities/:id',
    loadComponent: () => import('./zoho_support/reply/reply').then(m => m.Reply),
    canActivate: [authGuard]
  },

  // ==================== PARKING MANAGEMENT ROUTES ====================

  /**
   * Parking lots management
   * CRUD operations for parking facilities
   */
  {
    path: 'parkings',
    component: Parkings,
    pathMatch: 'full',
    canActivate: [authGuard, PermissionGuard]
  },

  /**
   * Parking lots management (alternative slug from API)
   * Maps parking-lots slug to parkings component
   */
  {
    path: 'parking-lots',
    loadComponent: () => import('./client_app/parking-lots/parking-lots').then(m => m.ParkingLots),
    pathMatch: 'full',
    canActivate: [authGuard, PermissionGuard]
  },

  /**
   * Specific parking lot detail with ID
   * View/manage individual parking lot
   */
  {
    path: 'parking-lots/:id',
    loadComponent: () => import('./client_app/parking-lots/parking-lots').then(m => m.ParkingLots),
    canActivate: [authGuard, PermissionGuard]
  },
  // Human-readable step routes (place before generic ':id' to avoid conflicts)
  { path: 'parkings/create', redirectTo: 'parkings/create/parking-type', pathMatch: 'full' },
  { path: 'parkings/create/parking-type', component: Parkings, canActivate: [authGuard, preventWizardBackGuard] },
  { path: 'parkings/create/location-address', component: Parkings, canActivate: [authGuard, preventWizardBackGuard] },
  { path: 'parkings/create/complete-details', component: Parkings, canActivate: [authGuard, preventWizardBackGuard] },
  { path: 'parkings/create/parking-details', component: Parkings, canActivate: [authGuard, preventWizardBackGuard] },
  { path: 'parkings/create/amenities', component: Parkings, canActivate: [authGuard, preventWizardBackGuard] },
  { path: 'parkings/create/working-hours', component: Parkings, canActivate: [authGuard, preventWizardBackGuard] },
  { path: 'parkings/create/assign-manager', component: Parkings, canActivate: [authGuard, preventWizardBackGuard] },
  { path: 'parkings/create/review', component: Parkings, canActivate: [authGuard, preventWizardBackGuard] },
  // Generic numeric step fallback under create to prevent wildcard redirect
  { path: 'parkings/create/step:stepNumber', component: Parkings, canActivate: [authGuard, preventWizardBackGuard] },
  // Generic slug fallback under create to prevent dashboard redirect on unknown slugs
  { path: 'parkings/create/:slug', component: Parkings, canActivate: [authGuard, preventWizardBackGuard] },
  {
    path: 'parkings/step:stepNumber',
    component: Parkings,
    canActivate: [authGuard, PermissionGuard]
  },

  /**
   * Specific parking lot management
   * Edit and manage individual parking facilities
   */
  {
    path: 'parkings/:id',
    component: Parkings,
    canActivate: [authGuard, PermissionGuard]
  },
  // {
  //   path: 'billing',
  //   loadComponent: () => import('./admin_app/modules/billing/billing').then(m => m.Billing),
  //   canActivate: [authGuard]
  // },
  // {
  //   path: 'billing/:id',
  //   loadComponent: () => import('./admin_app/modules/billing/billing').then(m => m.Billing),
  //   canActivate: [authGuard]
  // },
  {
    path: 'subscriptions',
    loadComponent: () => import('./admin_app/modules/subscriptions/subscriptions').then(m => m.Subscriptions),
    title: 'Subscriptions | Manage Plan',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'subscriptions/:id',
    loadComponent: () => import('./admin_app/modules/subscriptions/subscriptions').then(m => m.Subscriptions),
    title: 'Subscriptions | Manage Plan',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'plans',
    loadComponent: () => import('./admin_app/modules/plans/plans').then(m => m.Plans),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'plans/:id',
    loadComponent: () => import('./admin_app/modules/plans/plans').then(m => m.Plans),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'reports/:id',
    loadComponent: () => import('./client_app/reports/reports').then(m => m.Reports),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'report-view',
    loadComponent: () => import('./client_app/report-view/report-view').then(m => m.ReportView),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'report-view/:id',
    loadComponent: () => import('./client_app/report-view/report-view').then(m => m.ReportView),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'admin-full-report',
    loadComponent: () => import('./client_app/admin-full-report/admin-full-report').then(m => m.AdminFullReport),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'admin-full-report/:id',
    loadComponent: () => import('./client_app/admin-full-report/admin-full-report').then(m => m.AdminFullReport),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'sub-price',
    loadComponent: () => import('./admin_app/modules/sub-price/sub-price').then(m => m.SubPrice),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'sub-price/:id',
    loadComponent: () => import('./admin_app/modules/sub-price/sub-price').then(m => m.SubPrice),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'plan-price-group',
    loadComponent: () => import('./admin_app/modules/plan-price-group/plan-price-group').then(m => m.PlanPriceGroup),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'plan-price-group/:id',
    loadComponent: () => import('./admin_app/modules/plan-price-group/plan-price-group').then(m => m.PlanPriceGroup),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'subscribers',
    loadComponent: () => import('./admin_app/modules/subscribers/subscribers').then(m => m.Subscribers),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'subscribers/:id',
    loadComponent: () => import('./admin_app/modules/subscribers/subscribers').then(m => m.Subscribers),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'transactions',
    loadComponent: () => import('./admin_app/modules/transactions/transactions').then(m => m.Transactions),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'transactions/:id',
    loadComponent: () => import('./admin_app/modules/transactions/transactions').then(m => m.Transactions),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'organisations',
    loadComponent: () => import('./admin_app/modules/all-org/all-org').then(m => m.AllOrg),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'organisations/:id',
    loadComponent: () => import('./admin_app/modules/all-org/all-org').then(m => m.AllOrg),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'timezone-settings',
    loadComponent: () => import('./shared/components/global-timezone-settings/global-timezone-settings').then(m => m.GlobalTimezoneSettingsComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'timezone-settings/:id',
    loadComponent: () => import('./shared/components/global-timezone-settings/global-timezone-settings').then(m => m.GlobalTimezoneSettingsComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'timezone-demo',
    loadComponent: () => import('./shared/components/timezone-demo/timezone-demo').then(m => m.TimezoneDemoComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'timezone-demo/:id',
    loadComponent: () => import('./shared/components/timezone-demo/timezone-demo').then(m => m.TimezoneDemoComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  // Old format routes removed - use dynamic POV routes instead
  // Redirect guard will handle old format URLs
  {
    path: 'global-settings',
    loadComponent: () => import('./shared/components/global-settings/global-settings').then(m => m.GlobalSettingsComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'global-settings/:id',
    loadComponent: () => import('./shared/components/global-settings/global-settings').then(m => m.GlobalSettingsComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'role-navigation-demo',
    loadComponent: () => import('./shared/components/role-navigation-demo/role-navigation-demo').then(m => m.RoleNavigationDemoComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'role-navigation-demo/:id',
    loadComponent: () => import('./shared/components/role-navigation-demo/role-navigation-demo').then(m => m.RoleNavigationDemoComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'role-switcher-debug',
    loadComponent: () => import('./shared/components/role-switcher-debug/role-switcher-debug').then(m => m.RoleSwitcherDebugComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'role-switcher-debug/:id',
    loadComponent: () => import('./shared/components/role-switcher-debug/role-switcher-debug').then(m => m.RoleSwitcherDebugComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'role-switcher-test',
    loadComponent: () => import('./shared/components/role-switcher-test/role-switcher-test').then(m => m.RoleSwitcherTestComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'role-switcher-test/:id',
    loadComponent: () => import('./shared/components/role-switcher-test/role-switcher-test').then(m => m.RoleSwitcherTestComponent),
    canActivate: [authGuard, PermissionGuard]
  },
  /**
   * Old format settings route - for redirect guard to intercept
   * Redirect guard will redirect to new dynamic POV format: /:pov/:id/settings/:subRoute
   */
  {
    path: 'settings',
    component: SettingsMain,
    canActivate: [authGuard, PermissionGuard],
    children: [
      { path: '', component: Regions },
      // Routes accessible without POV (no redirect guard)
      { path: 'regions', component: Regions },
      { path: 'regions/:id', component: Regions },
      { path: 'language', loadComponent: () => import('./admin_app/settings/language/language').then(m => m.default) },
      { path: 'language/:id', loadComponent: () => import('./admin_app/settings/language/language').then(m => m.default) },
      { path: 'payment-gateway', loadComponent: () => import('./client_app/payment-gateway/payment-gateway').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'payment-gateway/:id', loadComponent: () => import('./client_app/payment-gateway/payment-gateway').then(m => m.default), canActivate: [authGuard, PermissionGuard] },
      { path: 'devices', loadComponent: () => import('./admin_app/settings/devices/devices').then(m => m.default) },
      { path: 'devices/:id', loadComponent: () => import('./admin_app/settings/devices/devices').then(m => m.default) },
      { path: 'thermal-printer', loadComponent: () => import('./admin_app/settings/thermal-printer/thermal-printer').then(m => m.ThermalPrinterComponent) },
      { path: 'thermal-printer/:id', loadComponent: () => import('./admin_app/settings/thermal-printer/thermal-printer').then(m => m.ThermalPrinterComponent) },
      // Routes with redirect guard (old format support)
      { path: 'org-profile/:id', loadComponent: () => import('./client_app/organization-profile/organization-profile').then(m => m.OrganizationProfile), canActivate: [ParkingRedirectGuard] },
      { path: 'organization-profile/:id', loadComponent: () => import('./client_app/organization-profile/organization-profile').then(m => m.OrganizationProfile), canActivate: [ParkingRedirectGuard] },
      { path: 'parking-profile/:parkingId', loadComponent: () => import('./client_app/parking-profile/parking-profile').then(m => m.ParkingProfile), canActivate: [ParkingRedirectGuard] },
      { path: 'team/:id', component: Team, canActivate: [PermissionGuard, RoleGuard, ParkingRedirectGuard] },
      { path: 'passes/:id', loadComponent: () => import('./admin_app/settings/passes/passes').then(m => m.Passes), canActivate: [ParkingRedirectGuard] },
      { path: 'pass-rates/:id', loadComponent: () => import('./admin_app/settings/passes/passes').then(m => m.Passes), canActivate: [ParkingRedirectGuard] },
      { path: 'vehicle-rates/:id', loadComponent: () => import('./admin_app/settings/vehicle-rate/vehicle-rate').then(m => m.default), canActivate: [ParkingRedirectGuard] },
      { path: 'facilities/:id', component: Facilities, canActivate: [ParkingRedirectGuard] },
      { path: 'facility-rates/:id', component: Facilities, canActivate: [ParkingRedirectGuard] },
      { path: 'integrations/:id', component: Integrations, canActivate: [ParkingRedirectGuard] },
      { path: 'hardware-integration/:id', component: Integrations, canActivate: [ParkingRedirectGuard] },
      { path: 'anpr-module/:id', component: Integrations, canActivate: [ParkingRedirectGuard] },
      { path: 'payment-options/:id', loadComponent: () => import('./client_app/payment-gateway/payment-gateway').then(m => m.default), canActivate: [authGuard, PermissionGuard, ParkingRedirectGuard] },
      { path: 'taxes/:id', loadComponent: () => import('./client_app/taxes/taxes').then(m => m.default), canActivate: [authGuard, PermissionGuard, ParkingRedirectGuard] },
      { path: 'data-import', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'data-import/:id', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'dataimport', redirectTo: 'data-import', pathMatch: 'full' },
      { path: 'dataimport/:id', redirectTo: 'data-import/:id', pathMatch: 'full' },
      { path: 'import-historical-data', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'import-historical-data/:id', loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport) },
      { path: 'data-export', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: 'data-export/:id', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: 'dataexport', redirectTo: 'data-export', pathMatch: 'full' },
      { path: 'dataexport/:id', redirectTo: 'data-export/:id', pathMatch: 'full' },
      { path: 'export-historical-data', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: 'export-historical-data/:id', loadComponent: () => import('./client_app/data-export/data-export').then(m => m.DataExport), title: 'Data Export | Parking Management System' },
      { path: ':id', component: Regions },
    ]
  },
  {
    path: 'setup-guide',
    loadComponent: () => import('./client_app/setup-guide/setup-guide').then(m => m.SetupGuide),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'setup-guide/:id',
    loadComponent: () => import('./client_app/setup-guide/setup-guide').then(m => m.SetupGuide),
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'data-import',
    loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport),
    title: 'Data Import | Parking Management System',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'data-import/:id',
    loadComponent: () => import('./client_app/data-import/data-import').then(m => m.DataImport),
    title: 'Data Import | Parking Management System',
    canActivate: [authGuard, PermissionGuard]
  },
  {
    path: 'dataimport',
    redirectTo: 'data-import',
    pathMatch: 'full'
  },
  {
    path: 'dataimport/:id',
    redirectTo: 'data-import/:id',
    pathMatch: 'full'
  },
  {
    path: 'data-export',
    redirectTo: 'settings/data-export',
    pathMatch: 'full'
  },
  {
    path: 'data-export/:id',
    redirectTo: 'settings/data-export/:id',
    pathMatch: 'full'
  },
  {
    path: 'dataexport',
    redirectTo: 'settings/data-export',
    pathMatch: 'full'
  },
  {
    path: 'dataexport/:id',
    redirectTo: 'settings/data-export/:id',
    pathMatch: 'full'
  },
  /**
   * No Parking Assigned page
   */
  {
    path: 'no-parking',
    loadComponent: () => import('./no-parking/no-parking').then(m => m.default),
    title: 'No Parking Assigned | Parking Management System',
    canActivate: [authGuard]
  },
  {
    path: 'no-parking/:id',
    loadComponent: () => import('./no-parking/no-parking').then(m => m.default),
    title: 'No Parking Assigned | Parking Management System',
    canActivate: [authGuard]
  },
  {
    path: 'no-organization',
    loadComponent: () => import('./no-organization/no-organization').then(m => m.default),
    title: 'No Organization Assigned | Parking Management System',
    canActivate: [authGuard]
  },
  {
    path: 'no-organization/:id',
    loadComponent: () => import('./no-organization/no-organization').then(m => m.default),
    title: 'No Organization Assigned | Parking Management System',
    canActivate: [authGuard]
  },

  /**
   * 404 Not Found page
   * Requires authentication but not permission check to avoid redirect loops
   */
  {
    path: 'not-found',
    loadComponent: () => import('./not-found/not-found').then(m => m.default),
    pathMatch: 'full',
    canActivate: [authGuard]
  },

  {
    path: '**',
    loadComponent: () => import('./not-found/not-found').then(m => m.default),
    // 404 Not Found page for unmatched routes
  }
];
